/**
 * App Events - Central Event Hub
 *
 * Subscribes to all pub/sub topics and handles application-wide events.
 * Acts as the main event listener and coordinator.
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { FEATURE_FLAGS } from '../../../../shared/featureFlags.js';
import {
  ROLE,
  SOURCE,
  PHASE,
  SINK,
  createTurn,
  normalizeTurn,
  subTopicOf,
  isCommand,
  displayTime,
  directiveOf
} from '../../../../shared/conversation.js';
import {
  hydrate,
  navIntent,
  capabilityTips,
  getTemplate,
  getAction,
  parseRoutable,
  matchPatterns,
  paramsFor,
  validateActionParams,
  NO_TEMPLATE,
  EXPLAIN
} from '../../../../shared/meta/meta_templates.js';

/**
 * Global Web Component Event Bridge
 * Listens to all custom events from web components and publishes to PubSub
 *
 * Web components can include a 'pubsubTopic' property in event.detail
 * to specify which PubSub topic to publish to.
 */
function initWebComponentEventBridge() {
  // Generic event handler for all web component custom events
  const handleWebComponentEvent = (eventType) => {
    return (e) => {
      const topic = e.detail?.pubsubTopic;

      if (topic) {
        // If web component specifies a topic, use it
        PubSub.publish(topic, e.detail);
      }
    };
  };

  // Register listeners for all web component event types
  document.addEventListener('nav-select', handleWebComponentEvent('nav-select'));
  document.addEventListener('logo-click', handleWebComponentEvent('logo-click'));
  document.addEventListener('nav-hover', handleWebComponentEvent('nav-hover'));
  document.addEventListener('nav-double-tap', handleWebComponentEvent('nav-double-tap'));
  document.addEventListener('nav-long-tap', handleWebComponentEvent('nav-long-tap'));
  document.addEventListener('message-sent', handleWebComponentEvent('message-sent'));
  // Affordance clicked inside a rendered template - see shared/wc/m5t-chat-workflow.js
  document.addEventListener('workflow-action', handleWebComponentEvent('workflow-action'));
  // ether5 card selection / iconic verbs. Inert until a card carries
  // data-ether5-topic; the bridge ignores a detail with no pubsubTopic.
  document.addEventListener('ether5-select', handleWebComponentEvent('ether5-select'));
  document.addEventListener('ether5-verb', handleWebComponentEvent('ether5-verb'));
}

/**
 * Initialize application event listeners
 */
export function initAppEvents() {
  console.log('[AppEvents] Initializing event listeners...');

  // ============================================
  // Application Events
  // ============================================

  PubSub.subscribe(AppBroadcast.APP_READY, (msg, data) => {
    console.log('[AppEvents] APP_READY:', data);
  });

  PubSub.subscribe(AppBroadcast.APP_ERROR, (msg, data) => {
    console.error('[AppEvents] APP_ERROR:', data);
    // TODO: Show error toast/modal
  });

  PubSub.subscribe(AppBroadcast.APP_SIGNOUT, (msg, data) => {
    console.log('[AppEvents] APP_SIGNOUT:', data);
    // Handled by mvvLegit.doSignout()
  });

  // ============================================
  // Route Navigation Events
  // ============================================

  PubSub.subscribe(AppBroadcast.ROUTE_NAV_BEFORE, (msg, data) => {
    console.log('[AppEvents] ROUTE_NAV_BEFORE:', data);
    // Show loading indicator
    PubSub.publish(AppBroadcast.UI_LOADING_START);
  });

  PubSub.subscribe(AppBroadcast.ROUTE_NAV_AFTER, (msg, data) => {
    console.log('[AppEvents] ROUTE_NAV_AFTER:', data);
    // Hide loading indicator
    PubSub.publish(AppBroadcast.UI_LOADING_END);
  });

  PubSub.subscribe(AppBroadcast.ROUTE_NAV_ERROR, (msg, data) => {
    console.error('[AppEvents] ROUTE_NAV_ERROR:', data);
    PubSub.publish(AppBroadcast.APP_ERROR, {
      message: 'Navigation error',
      details: data
    });
  });

  // ============================================
  // Authentication Events
  // ============================================

  PubSub.subscribe(AppBroadcast.AUTH_SIGNIN_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNIN_SUCCESS:', data);
    // mvvLegit will handle navigation to home route
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNIN_FAIL, (msg, data) => {
    console.error('[AppEvents] AUTH_SIGNIN_FAIL:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: data.message || 'Sign in failed',
      type: 'danger'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNUP_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNUP_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Account created! Please check your email to verify.',
      type: 'success'
    });
    // Navigation to verf-link is handled by mvvLegit.doUNVERF() (single path).
    // Do NOT also publish AUTH_VERIFY_LINK here — the duplicate navigation
    // caused competing route transitions after signup.
  });

  PubSub.subscribe(AppBroadcast.AUTH_VERIFY_LINK, (msg, data) => {
    console.log('[AppEvents] AUTH_VERIFY_LINK: Navigating to verf-link route');
    if (window.router) {
      window.router.navigate('/verf-link');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNUP_FAIL, (msg, data) => {
    console.error('[AppEvents] AUTH_SIGNUP_FAIL:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: data.message || 'Sign up failed',
      type: 'danger'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNOUT_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNOUT_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Signed out successfully',
      type: 'info'
    });
  });

  // REMOVED: This event handler was causing unwanted redirect to signin after successful signin
  // Navigation to signin is handled by AuthController when needed (e.g., token expiry)
  // PubSub.subscribe(AppBroadcast.AUTH_SIGNIN, (msg, data) => {
  //   console.log('[AppEvents] AUTH_SIGNIN: Navigating to signin route');
  //   if (window.router) {
  //     window.router.navigate('/signin');
  //   }
  // });

  PubSub.subscribe(AppBroadcast.AUTH_SIGNUP, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNUP: Navigating to signup route');
    if (window.router) {
      window.router.navigate('/signup');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_FORGOT, (msg, data) => {
    console.log('[AppEvents] AUTH_FORGOT: Navigating to forgot route');
    if (window.router) {
      window.router.navigate('/forgot');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_FORGOT_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_FORGOT_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Password reset email sent!',
      type: 'success'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_RESETHASH_NAV, (msg, data) => {
    console.log('[AppEvents] AUTH_RESETHASH_NAV: Navigating to resethash route');
    if (window.router) {
      window.router.navigate('/resethash');
    }
  });

  PubSub.subscribe(AppBroadcast.AUTH_RESETHASH_SUCCESS, (msg, data) => {
    console.log('[AppEvents] AUTH_RESETHASH_SUCCESS:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Password reset successfully! Please sign in.',
      type: 'success'
    });
  });

  PubSub.subscribe(AppBroadcast.AUTH_TOKEN_EXPIRED, (msg, data) => {
    console.warn('[AppEvents] AUTH_TOKEN_EXPIRED:', data);
    PubSub.publish(AppBroadcast.UI_TOAST, {
      message: 'Session expired. Please sign in again.',
      type: 'warning'
    });
    // mvvLegit will handle automatic sign out and navigation
  });

  // ============================================
  // I18N Events
  // ============================================

  PubSub.subscribe(AppBroadcast.I18N_SET_LANG, (msg, data) => {
    console.log('[AppEvents] I18N_SET_LANG:', data);

    const { langCode } = data;

    if (!langCode) {
      console.error('[AppEvents] No langCode provided in I18N_SET_LANG event');
      return;
    }

    // Call I18nController to change language
    if (window.I18nController && typeof window.I18nController.setLanguage === 'function') {
      try {
        window.I18nController.setLanguage(langCode);
        console.log('[AppEvents] Language changed successfully to:', langCode);

        // Optionally publish success event
        PubSub.publish(AppBroadcast.UI_TOAST, {
          message: `Language changed to ${langCode}`,
          type: 'success'
        });
      } catch (error) {
        console.error('[AppEvents] Error changing language:', error);
        PubSub.publish(AppBroadcast.UI_TOAST, {
          message: `Failed to change language: ${error.message}`,
          type: 'danger'
        });
      }
    } else {
      console.error('[AppEvents] I18nController not available');
    }
  });

  // ============================================
  // CONVERSATION Events
  // ============================================

  // One subscriber at the parent topic; the leaf selects sinks, and an explicit
  // `sinks` array on the envelope overrides it. See shared/conversation.js.
  PubSub.subscribe('CONVERSATION.PROMPT', (msg, data) => {
    const turn = normalizeTurn(data, { role: ROLE.USER, subTopic: subTopicOf(msg) });

    if (!turn.text) {
      console.warn('[AppEvents] CONVERSATION.PROMPT received without text', data);
      return;
    }

    // turnId is logged so a duplicated delivery is distinguishable from a
    // duplicated send: same id means one publish reached us twice, different
    // ids mean two publishes were made.
    console.log(`[AppEvents] PROMPT ${turn.sinks.join('+')} [${turn.source}] ${turn.turnId}:`, turn.text);

    if (turn.sinks.includes(SINK.STAGE)) {
      // Stage the text in the prompt area for the user to edit before sending.
      const promptArea = document.querySelector('m5t-chat-prompt-area');
      if (promptArea?.setMessage) promptArea.setMessage(turn.text);
      else console.warn('[AppEvents] STAGE sink: no m5t-chat-prompt-area on this route');
    }

    if (turn.sinks.includes(SINK.CANVAS)) {
      renderTurn(turn);
      PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
    }

    if (turn.sinks.includes(SINK.AGENT)) {
      // Commands are intercepted for every agent-bound prompt, not just the
      // AGENT_CANVAS leaf, so `::` behaves the same however it was published.
      if (isCommand(turn.text)) runCommand(turn);
      else routeThenPrompt(turn);
    }
  });

  PubSub.subscribe('CONVERSATION.RESPONSE', (msg, data) => {
    const turn = normalizeTurn(data, { role: ROLE.ASSISTANT, subTopic: subTopicOf(msg) });

    // A waiting turn carries no text on purpose - the phrases live in
    // <m5t-chat-waiting>, not the envelope - so it is exempt from the
    // empty-delta guard, which exists to catch malformed publishes.
    if (!turn.text && turn.phase !== PHASE.END && !turn.meta?.waiting) {
      console.warn('[AppEvents] CONVERSATION.RESPONSE received without text', data);
      return;
    }

    if (turn.sinks.includes(SINK.CANVAS)) renderTurn(turn);

    if (turn.sinks.includes(SINK.WORKFLOW)) runWorkflow(turn);
  });

  // ============================================
  // UI Events
  // ============================================

  PubSub.subscribe(AppBroadcast.UI_LOADING_START, (msg, data) => {
    // Show loading indicator
    const loader = document.getElementById('app-loader');
    if (loader) loader.classList.add('active');
  });

  PubSub.subscribe(AppBroadcast.UI_LOADING_END, (msg, data) => {
    // Hide loading indicator
    const loader = document.getElementById('app-loader');
    if (loader) loader.classList.remove('active');
  });

  PubSub.subscribe(AppBroadcast.UI_TOAST, (msg, data) => {
    // TODO: Implement toast notification system
    console.log('[AppEvents] UI_TOAST:', data);
  });

  PubSub.subscribe(AppBroadcast.UI_WELCOME_BACK_CLICKED, () => {
    console.log('[AppEvents] UI_WELCOME_BACK_CLICKED');
    const sMsg = '0.1'; // TODO: get this from meta
    const curYear = new Date().getFullYear();
    if (sMsg && typeof neodigmToast !== 'undefined') {
      neodigmToast.q(`${sMsg} __devops_ts__|© ${curYear} Mach Five Tech`, 'night');
    }
  });

  // ============================================
  // Web Component Events Bridge
  // ============================================

  // Listen to all custom events from web components and bridge to PubSub
  initWebComponentEventBridge();

  // ============================================
  // M5T Primary Navigation Events
  // ============================================

  // Single high-level subscriber with switch for sub-topics
  PubSub.subscribe('M5T_PRIMARY_NAV', (msg, data) => {
    // Extract sub-topic from message (e.g., "M5T_PRIMARY_NAV.TAP" -> "TAP")
    const subTopic = msg.split('.')[1];

    //console.log(`[AppEvents] M5T_PRIMARY_NAV.${subTopic}:`, data);

    switch(subTopic) {
      case 'TAP':
        handlePrimaryNavTap(data);
        break;

      case 'LOGO_TAP':
        handlePrimaryNavLogoTap(data);
        break;

      case 'HOVER':
        // Handle hover events if needed
        break;

      case 'DOUBLE_TAP':
        // Handle double tap events if needed
        break;

      case 'LONG_TAP':
        // Handle long tap events if needed
        break;

      default:
        console.log(`[AppEvents] Unhandled M5T_PRIMARY_NAV sub-topic: ${subTopic}`);
        break;
    }
  });

  // Handler for navigation tap events
  function handlePrimaryNavTap(data) {
    const { token, level, primaryToken } = data;

    switch(token) {
      // Profile secondary navigation - Password Reset
      case 'RESET_PASSWORD':
        console.log('[AppEvents] Password reset requested');
        PubSub.publish(AppBroadcast.AUTH_RESETHASH_NAV);
        break;

      // Profile secondary navigation - Sign Out
      case 'SIGN_OUT':
        console.log('[AppEvents] Sign out requested');
        if (window.router) {
          window.router.navigate('/signout');
        }
        break;

      // Default handler - a nav token may claim a template, otherwise just log
      default: {
        const intent = navIntent(token);

        if (intent) {
          console.log(`[AppEvents] Navigation: ${level} -> ${token} asks "${intent.prompt}"`);

          // One turn, two payloads - the split the sinks exist for. The canvas
          // (and the transcript) get English; the agent gets the instruction, so
          // an unambiguous click is never re-derived by a probabilistic router.
          PubSub.publish(AppBroadcast.CONVERSATION_PROMPT_AGENT_CANVAS, createTurn({
            text: intent.prompt,
            directive: { componentId: intent.id, params: {} },
            source: SOURCE.AUTOMATION,
            sinks: [SINK.CANVAS, SINK.AGENT],
            meta: { origin: 'nav', navToken: token }
          }));
          break;
        }

        console.log(`[AppEvents] Navigation: ${level} -> ${token}`, { primaryToken });
        break;
      }
    }
  }

  // Handler for logo tap events
  function handlePrimaryNavLogoTap(data) {
    const sMsg = '0.1'; // TODO: get this from meta
    const curYear = new Date().getFullYear();
    if (sMsg && typeof neodigmToast !== 'undefined') {
      neodigmToast.q(`${sMsg} __devops_ts__|© ${curYear} Mach Five Tech`, 'night', 5000);
    }
  }

  // ============================================
  // LLM Events
  // ============================================

  // LLM_DO_DOWNLOAD: Trigger model download/load
  PubSub.subscribe(AppBroadcast.LLM_DO_DOWNLOAD, (msg, data) => {
    console.log('[AppEvents] LLM_DO_DOWNLOAD:', data);

    if (window.LLMController) {
      window.LLMController.loadModel();
    } else {
      console.error('[AppEvents] LLMController not available');
    }
  });

  // LLM_STATUS: Progress updates during download
  PubSub.subscribe(AppBroadcast.LLM_STATUS, (msg, data) => {
    console.log('[AppEvents] LLM_STATUS:', data);

    // Display progress in toast or status area
    if (data && typeof neodigmToast !== 'undefined') {
      const progressText = data.text || `${data.progressPercent}% complete`;
      // Don't show toast for every progress update (too spammy)
      // UI components can subscribe directly for real-time updates
    }
  });

  // LLM_COMPLETE: Download/initialization complete
  PubSub.subscribe(AppBroadcast.LLM_COMPLETE, (msg, data) => {
    console.log('[AppEvents] LLM_COMPLETE:', data);

    // Toast removed - model loading is silent background process

    // Publish to CONVERSATION.RESPONSE.CANVAS to show in chat
    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: `✓ AI Model Ready: ${data.modelId}`,
      phase: PHASE.END,
      meta: { modelId: data.modelId }
    }));
  });

  // LLM_ERROR: Error during download/init/generation
  PubSub.subscribe(AppBroadcast.LLM_ERROR, (msg, data) => {
    console.error('[AppEvents] LLM_ERROR:', data);

    const errorMsg = `AI Error: ${data.error || 'Unknown error'}`;

    if (typeof neodigmToast !== 'undefined') {
      neodigmToast.q(errorMsg, 'danger', 8000);
    }

    // Show error in chat canvas. When the failure belongs to an in-flight turn,
    // reuse that turn's bubble id so the error replaces the partial answer
    // instead of stacking a second bubble beside it.
    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      id: data?.responseId,
      turnId: data?.turnId,
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: `✗ ${errorMsg}`,
      phase: PHASE.ERROR
    }));
  });

  // LLM_PROMPT: Send prompt to local LLM
  PubSub.subscribe(AppBroadcast.LLM_PROMPT, (msg, data) => {
    console.log('[AppEvents] LLM_PROMPT:', data);

    if (window.LLMController && data?.message) {
      window.LLMController.chat(data.message, { turnId: data.turnId });
    } else {
      console.error('[AppEvents] LLMController not available or no message provided');
    }
  });

  // LLM_RESPONSE: translate the service stream into canvas turns.
  // The `done` frame is forwarded now (it used to be dropped): it carries
  // phase END, which is what closes the bubble and releases its id.
  PubSub.subscribe(AppBroadcast.LLM_RESPONSE, (msg, data) => {
    if (!data) return;
    if (!data.done && !data.fullContent) return;

    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      id: data.responseId,
      turnId: data.turnId,
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: data.fullContent ?? '',
      phase: data.done ? PHASE.END : PHASE.DELTA,
      meta: data.meta ?? {}
    }));
  });

  // LLM_CANCEL: Cancel ongoing generation
  PubSub.subscribe(AppBroadcast.LLM_CANCEL, (msg, data) => {
    console.log('[AppEvents] LLM_CANCEL');

    if (window.LLMController) {
      window.LLMController.cancelGeneration();
    }
  });

  // ============================================
  // Cleanup Events
  // ============================================

  // Clear session_user from localStorage on signout
  PubSub.subscribe(AppBroadcast.AUTH_SIGNOUT, (msg, data) => {
    console.log('[AppEvents] AUTH_SIGNOUT: Clearing session_user from localStorage');
    localStorage.removeItem(FEATURE_FLAGS.FF_SSE_LS_SESSION_USER);
  });

  console.log('[AppEvents] Event listeners initialized');
}

// ============================================
// Chat Canvas Helpers
// ============================================

/**
 * id -> bubble element. Keying on the envelope's id is what lets two streams
 * (an answer and a download-progress line, say) run at once: the old code
 * updated "the first child if it is a response", so whichever stream wrote last
 * captured the other's bubble.
 */
const bubbles = new Map();

/**
 * Render or update the bubble for a turn.
 *
 * Uses prepend() because #chat-messages is column-reverse - first child renders
 * at the bottom.
 */
function renderTurn(turn) {
  const chatMessages = document.getElementById('chat-messages');
  if (!chatMessages) {
    console.warn('[AppEvents] chat-messages element not found - may not be on home route');
    return null;
  }

  // Any real answer retires the waiting bubble for its turn - a rendered view, a
  // streamed prose delta, or an error all arrive here.
  const isWaiting = !!turn.meta?.waiting;
  if (turn.role === ROLE.ASSISTANT && !isWaiting) dismissWaiting(turn.turnId);

  let bubble = bubbles.get(turn.id);

  // A route change replaces innerHTML, orphaning anything we cached.
  if (bubble && !bubble.isConnected) {
    bubbles.delete(turn.id);
    bubble = null;
  }

  // A turn carrying rendered template output goes to the trusting element; model
  // text never does. The trust boundary is the element type - see
  // shared/wc/m5t-chat-workflow.js.
  //
  // Keyed on rendered `html`, not on `componentId` alone: a nav click publishes a
  // USER turn that names its view up front, and that must still draw a plain
  // prompt bubble rather than an empty workflow panel.
  const isWorkflow = turn.role === ROLE.ASSISTANT && typeof turn.meta?.html === 'string';

  if (!bubble) {
    bubble = document.createElement(
      isWaiting ? 'm5t-chat-waiting'
        : isWorkflow ? 'm5t-chat-workflow'
          : turn.role === ROLE.ASSISTANT ? 'm5t-chat-response'
            : 'm5t-chat-prompt'
    );
    bubble.setAttribute('data-turn-id', turn.turnId);
    bubble.setAttribute('data-message-id', turn.id);
    bubble.setAttribute('timestamp', turn.timestamp);

    // Read once at creation: the registry cannot change mid-wait, and re-reading
    // on a later paint would restart the rotation.
    if (isWaiting) bubble.phrases = capabilityTips();

    bubbles.set(turn.id, bubble);
    chatMessages.prepend(bubble);
  }

  if (isWaiting) {
    // Nothing to update - the element owns its own rotation until it is removed.
    return bubble;
  }

  if (isWorkflow) {
    // Content is a property, not an attribute - a rendered view is far too large
    // for one. `text` still carries the transcript line, so a turn replayed
    // without the element defined degrades to a readable summary.
    bubble.setContent?.(turn.meta.html ?? '', { templateId: turn.meta.componentId });
  } else {
    bubble.setAttribute('message', turn.text);
  }

  if (turn.phase === PHASE.END || turn.phase === PHASE.ERROR) {
    bubble.setAttribute('phase', turn.phase);
    // The stream is finished; nothing else will address this bubble.
    bubbles.delete(turn.id);
  }

  return bubble;
}

/**
 * Run a `::` command and publish its output as an assistant turn on the same
 * turnId, so a command exchange correlates exactly like a model exchange.
 */
async function runCommand(turn) {
  console.log('[AppEvents] CLI command:', turn.text);

  try {
    const CLIController = (await import('../services/cliController.js')).default;
    const entity = window.AuthController?.getEntity();
    const response = await CLIController.process(turn.text, entity);

    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      turnId: turn.turnId,
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: response.message,
      phase: PHASE.END,
      meta: { command: true }
    }));
  } catch (err) {
    console.error('[AppEvents] CLI command failed', err);

    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      turnId: turn.turnId,
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: `Command failed: ${err.message}`,
      phase: PHASE.ERROR
    }));
  }

  PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
}

/**
 * Answer an out-of-scope request without calling the model.
 *
 * Costs no generation, so it is instant, and it cannot fabricate - the prose path
 * is where every observed hallucination came from, including a confident claim to
 * have created an app that did not exist.
 *
 * The suggestions come from the registry (capabilityTips), so a dead end doubles
 * as the discovery surface a conversational UI otherwise lacks: nothing on screen
 * advertises what it understands.
 */
function declineOutOfScope(turn) {
  const tips = capabilityTips().slice(0, 3);

  const suggestions = tips.length
    ? '\n\n' + tips.map((t) => {
      const ask = t.replace(/^Try asking:\s*"?|"$/g, '');
      return `- [${ask}](#)`;
    }).join('\n')
    : '';

  PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
    turnId: turn.turnId,
    role: ROLE.ASSISTANT,
    source: SOURCE.SYSTEM,
    text: `I can only help with administering this account.${suggestions}`,
    phase: PHASE.END,
    meta: { declined: true }
  }));

  // Recorded so a follow-up ("why not?") has a referent, and so the transcript
  // shows a refusal rather than a gap.
  window.LLMController?.noteUser?.(turn.text);
  window.LLMController?.noteAssistant?.('Declined - the request was outside this application.');

  PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
}

/** Bubble id for the waiting state of a turn. Derived, so it needs no bookkeeping. */
const waitingId = (turnId) => `wait_${turnId}`;

/**
 * Occupy the silence while a non-streaming pass runs.
 *
 * Published rather than inserted directly so it lands on a [[pubsub-vcr]] tape
 * like everything else, and so the canvas stays the only thing touching the DOM.
 * PHASE.DELTA keeps renderTurn's cache entry alive - an END would release it and
 * leave nothing to dismiss.
 */
function showWaiting(turn) {
  // Idempotent. A routed prompt to a fetching template calls this twice - once
  // to cover the routing pass, once to cover the fetch - and while the second
  // render is already a no-op, the scroll it published was not.
  if (bubbles.has(waitingId(turn.turnId))) return;

  PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
    id: waitingId(turn.turnId),
    turnId: turn.turnId,
    role: ROLE.ASSISTANT,
    source: SOURCE.SYSTEM,
    text: '',
    phase: PHASE.DELTA,
    meta: { waiting: true }
  }));

  PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
}

/**
 * Remove the waiting bubble for a turn, if one is up.
 *
 * Called from renderTurn for every non-waiting assistant turn, which is the one
 * choke point all three outcomes pass through - a rendered view, a streamed
 * prose answer, and an error all publish an assistant turn on the same turnId.
 */
function dismissWaiting(turnId) {
  const id = waitingId(turnId);
  const bubble = bubbles.get(id);
  if (!bubble) return;

  bubbles.delete(id);
  bubble.remove();
}

/**
 * Ask whether a registered template answers this prompt better than prose, then
 * send it one way or the other.
 *
 * The routing pass is grammar-constrained to the registry's enum, so a hit is
 * always renderable. Anything else - flag off, no templates, model still
 * loading, malformed answer - falls through to prose, because a routing miss
 * must never cost the user their reply.
 */
async function routeThenPrompt(turn) {
  // Already instructed by whoever published it (a nav click names its own view).
  // Skipping the pass is the point: it costs nothing, works before the model has
  // finished loading, and cannot misroute.
  let hit = directiveOf(turn);

  // Deterministic first. A stock phrase has one correct answer and should never
  // be a coin toss - this is the same trade nav makes, applied to typed input.
  // It also costs nothing: no prefill, no decode, no waiting bubble.
  if (!hit) {
    const matched = matchPatterns(turn.text);
    if (matched) {
      console.log(`[AppEvents] matched by pattern: ${matched.id}`);
      hit = { componentId: matched.id, action: null, params: {} };
    }
  }

  if (!hit && FEATURE_FLAGS.FF_CLIENT_TEMPLATE_ROUTING) {
    // Only phrasings no pattern covers reach the model. The routing pass does not
    // stream, so this stretch is otherwise completely silent - shown before the
    // await, dismissed by renderTurn when any answer lands.
    showWaiting(turn);
    hit = await window.LLMController?.route?.(turn.text);
  }

  // null means the pass could not RUN (model loading, generation in flight).
  // Falling through to prose is the safe read - we do not know enough to decline.
  if (!hit) {
    PubSub.publish(AppBroadcast.LLM_PROMPT, { message: turn.text, turnId: turn.turnId });
    return;
  }

  // An explicit decline. No generation at all: instant, and it closes the path
  // every observed hallucination came from.
  if (hit.componentId === NO_TEMPLATE) {
    declineOutOfScope(turn);
    return;
  }

  // The model asked to answer in prose about this application.
  if (hit.componentId === EXPLAIN) {
    PubSub.publish(AppBroadcast.LLM_PROMPT, { message: turn.text, turnId: turn.turnId });
    return;
  }

  // Routing returns a routable id, which may name an action on a view
  // ("MY_APPS:CREATE_APP"). Arguments are pulled in a second, narrower pass -
  // only now, when we know which action and therefore which schema.
  const { componentId, action } = parseRoutable(hit.componentId);

  // Extract for anything that declares params - a VIEW can need them too
  // (APP_SNIPPET needs a name), and gating on `action` meant a view's params
  // were silently never filled.
  const declared = paramsFor(componentId, action);
  const needsExtraction = declared && Object.keys(declared).length && !Object.keys(hit.params).length;

  hit = {
    componentId,
    action,
    params: needsExtraction
      ? await window.LLMController?.extractParams?.(componentId, action, turn.text) ?? {}
      : hit.params
  };

  // `text`, never the directive - machine instructions in the transcript teach
  // the model to emit machine instructions. route() does not touch history and
  // chat() never runs on this path, so the question is recorded here;
  // runWorkflow() records the answer.
  window.LLMController?.noteUser?.(turn.text);

  PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_WORKFLOW, createTurn({
    turnId: turn.turnId,
    role: ROLE.ASSISTANT,
    source: SOURCE.SYSTEM,
    sinks: [SINK.WORKFLOW],
    directive: hit
  }));
}

/**
 * Recently completed actions, keyed by target + arguments.
 *
 * Actions mutate. Views can be rendered twice harmlessly; creating the same app
 * twice cannot be undone by re-rendering, so identical calls inside a short
 * window are collapsed. This covers a double-click on a New button, a stray
 * second publish, and a user re-sending an unchanged prompt - none of which
 * should produce two records.
 *
 * Deliberately keyed on arguments, not on turnId: a redelivered envelope and a
 * genuine double-send do not share a turnId, but they do share their payload.
 */
const recentActions = new Map();
const ACTION_DEDUPE_MS = 8000;

function actionKey(componentId, actionId, params) {
  return `${componentId}:${actionId}:${JSON.stringify(params)}`;
}

/**
 * Run a registered action, reporting failure to the canvas.
 *
 * Validation happens here rather than in the grammar. The extraction schema can
 * force the *shape* of an answer but not its truth - a model asked for a URL the
 * user never supplied will happily emit a plausible one. So a missing or
 * malformed argument becomes a question back to the user, never a guess: this is
 * a mutation, and inventing an argument for a mutation is the one failure that
 * cannot be undone by re-rendering.
 *
 * @returns {Promise<{ok: boolean}>}
 */
async function runAction(turn, componentId, actionId, rawParams) {
  const action = getAction(componentId, actionId);

  if (!action) {
    console.warn(`[AppEvents] unknown action ${componentId}:${actionId}`);
    return { ok: false };
  }

  const checked = validateActionParams(componentId, actionId, rawParams);

  if (!checked.ok) {
    console.warn(`[AppEvents] ${componentId}:${actionId} rejected`, checked.errors, rawParams);

    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      turnId: turn.turnId,
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: `${action.needs ?? 'I need a bit more to do that.'}\n\n${checked.errors.map((e) => `- ${e}`).join('\n')}`,
      phase: PHASE.END
    }));

    // The model is told what was asked and what was missing, so a follow-up
    // supplying the value reads as a continuation rather than a fresh request.
    window.LLMController?.noteAssistant?.(
      `Could not ${actionId} - ${checked.errors.join('; ')}. Asked the user to supply it.`
    );

    PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
    return { ok: false };
  }

  const key = actionKey(componentId, actionId, checked.params);
  const now = Date.now();

  for (const [k, at] of recentActions) {
    if (now - at > ACTION_DEDUPE_MS) recentActions.delete(k);
  }

  if (recentActions.has(key)) {
    console.warn(`[AppEvents] suppressed duplicate ${componentId}:${actionId} within ${ACTION_DEDUPE_MS}ms`, checked.params);
    // ok:true so the owning view still re-renders - the user sees the result of
    // the first run rather than an error for something that did succeed.
    return { ok: true };
  }

  // Claimed before awaiting, so a second call that arrives while the first is
  // still in flight is suppressed too - the window that matters most.
  recentActions.set(key, now);

  try {
    await action.run(checked.params);
    return { ok: true };
  } catch (err) {
    // A failed attempt should be retryable immediately.
    recentActions.delete(key);

    console.error(`[AppEvents] ${componentId}:${actionId} failed`, err);

    PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
      turnId: turn.turnId,
      role: ROLE.ASSISTANT,
      source: SOURCE.SYSTEM,
      text: `That did not go through: ${err.message}`,
      phase: PHASE.ERROR
    }));

    window.LLMController?.noteAssistant?.(`${actionId} failed: ${err.message}. Nothing was changed.`);
    PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
    return { ok: false };
  }
}

/**
 * Render a registered template into the canvas.
 *
 * The instruction arrives as `directive`, never parsed back out of `text` - so a
 * VCR tape replays the same view without re-running the model.
 *
 * Two publishes, deliberately:
 *   - CANVAS gets the markup, so the user sees the view.
 *   - the model transcript gets `transcript`, so the *next* turn has a referent.
 *     Without it the model never learns anything was shown and "sort that by
 *     spend" lands on nothing.
 *
 * The outbound turn carries `meta`, not a directive: it is a record of what was
 * rendered, not an instruction to render it again.
 */
async function runWorkflow(turn) {
  const instruction = directiveOf(turn);

  if (!instruction) {
    console.warn('[AppEvents] WORKFLOW sink without a directive - nothing to render', turn);
    return;
  }

  const { componentId, action, params } = instruction;
  console.log('[AppEvents] WORKFLOW render:', componentId, action ?? '(view)', params);

  // An action runs first, then the owning view re-renders to show its effect.
  // That ordering is what makes the two paths isomorphic: whether the user
  // clicked New or typed "create an app named X", both end on the same grid.
  if (action) {
    const outcome = await runAction(turn, componentId, action, params);
    if (!outcome.ok) return; // already reported to the canvas
  }

  // A fetching template has to cross the network before there is anything to
  // show, and the API is on a host that can cold-start for tens of seconds. The
  // routed path already has a waiting bubble up; a nav click does not, and
  // showWaiting is keyed on turnId so calling it twice is a no-op. Skipped for
  // synchronous templates, where it would only flicker.
  if (getTemplate(componentId)?.fetch) showWaiting(turn);

  const result = await hydrate(componentId, params, turn.meta?.payload ?? null);

  if (result.errors.length) {
    console.warn(`[AppEvents] template "${componentId}" reported:`, result.errors);
  }

  // Republished on the CANVAS leaf - a sibling of WORKFLOW, so this cannot
  // re-enter the branch above.
  PubSub.publish(AppBroadcast.CONVERSATION_RESPONSE_CANVAS, createTurn({
    turnId: turn.turnId,
    role: ROLE.ASSISTANT,
    source: SOURCE.SYSTEM,
    text: result.transcript,
    phase: result.ok ? PHASE.END : PHASE.ERROR,
    meta: { componentId, html: result.html, params }
  }));

  window.LLMController?.noteAssistant?.(result.transcript);

  PubSub.publish(AppBroadcast.CANVAS_DO_SCROLL);
}
